import { KLUCZE_POL, pustyWiersz, type KluczPola, type SurowyWiersz } from './import-pracownikow';

/**
 * CZYTANIE ARKUSZA Z PRACOWNIKAMI.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ ZAMÓWIONE 04.09.2026: „powinno wykryć wszystkie arkusze i pozwolić wybrać odpowiedni oraz
 *  pozwolić zmapować kolumny na te kilka wymaganych parametrów".
 *
 *  ⚠️ `exceljs` JUŻ JEST W ZALEŻNOŚCIACH i jest ładowany DYNAMICZNIE — dokładnie tak, jak robi to
 *  eksport (`lib/export.ts`). Biblioteka buduje w pamięci model obiektowy każdej komórki, więc
 *  w głównej paczce nie ma po co siedzieć: import pracowników robi się raz na wdrożenie klienta.
 *
 *  ⚠️ WSZYSTKO CZYTAMY JAKO TEKST. To nie jest wygoda, a wymóg: `Users.USERNUMBER` jest napisem
 *  i realnie ma wiodące zera (w arkuszu Magny numery mają postać `000001234`). Przepuszczenie tej kolumny przez
 *  liczbę dałoby pracownika pod numerem `1234`, czyli pod INNYM kluczem niż ten na karcie.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

export type Arkusz = {
  nazwa: string;
  /** Wiersze jako tekst, bez pustych na końcu. Pierwszy może być nagłówkiem — o tym decyduje ekran. */
  wiersze: string[][];
  /**
   * Które kolumny miały w arkuszu wartości LICZBOWE.
   *
   * ⚠️⚠️ ISTNIEJE DLA WIODĄCYCH ZER I JEST JEDYNĄ RZECZĄ, KTÓRA MOŻE O NICH POWIEDZIEĆ. Jeśli
   * klient zapisał numer karty jako liczbę, zer NIE MA JUŻ W PLIKU i nie da się ich odtworzyć —
   * `000001234` jest wtedy w arkuszu wartością `1234`. Wczytanie tego bez słowa założyłoby
   * pracownika pod innym kluczem, więc okno musi ostrzec. W pliku Magny ta kolumna jest tekstowa
   * (sprawdzone) i ostrzeżenia nie będzie.
   */
  kolumnyLiczbowe: number[];
  /**
   * RODZAJ KAŻDEJ KOMÓRKI: `t` tekst, `n` liczba, `b` wartość LOGICZNA, `''` pusta.
   *
   * ═══════════════════════════════════════════════════════════════
   *  ⚠️⚠️ ISTNIEJE Z POWODU JEDNEGO ZMIERZONEGO PLIKU (`Rękawiczki dostęp.xlsx`, 04.09.2026)
   *  i jest to najważniejsza rzecz, jaką o arkuszach klientów wiemy. Zgłoszenie brzmiało: „zamiast
   *  1 wiersza dostajemy 2500 wierszy i nie damy rady tego zaimportować".
   *
   *  PRZYCZYNA, ODCZYTANA Z FORMUŁ: arkusz `Arkusz importowy` jest GENEROWANY, a jego kolumna
   *  numeru karty to `=IF('Grupy użytkowników'!Cn=1, CONCAT(...), )` — `IF` BEZ GAŁĘZI „w przeciwnym
   *  razie", więc dla każdego wiersza, gdzie warunek nie zachodzi, Excel zwraca logiczne FAŁSZ.
   *  Formuła jest rozciągnięta do wiersza 2500, kolumna grupy to `=Tabela2[...]` z pustej komórki
   *  (czyli liczba 0), a znaczniki `1,1,1,3` są wpisane na sztywno.
   *
   *  ⚠️⚠️ DLATEGO TE WIERSZE WYGLĄDAJĄ NA WYPEŁNIONE i żaden warunek „czy komórki są puste" ich
   *  nie odsieje. Odsiewa je RODZAJ komórki: wartość logiczna w kolumnie numeru karty nie jest
   *  numerem karty — `Users.USERNUMBER` jest TEKSTEM i nikt nie wpisuje tam „FAŁSZ".
   *
   *  ⚠️ WARTOŚCI LOGICZNE ZOSTAJĄ TEKSTEM W `wiersze`, nie zamieniamy ich na pustki. Kolumna
   *  znacznika naprawdę może być w arkuszu polem logicznym, a wtedy FAŁSZ znaczy „nie" — zamiana
   *  na pustkę podstawiłaby domyślną `1`, czyli odwróciłaby znaczenie. Rodzaj trzymamy OSOBNO
   *  właśnie po to, żeby jedna decyzja nie psuła drugiej.
   * ═══════════════════════════════════════════════════════════════
   */
  rodzaje: RodzajKomorki[][];
};

export type RodzajKomorki = 't' | 'n' | 'b' | '';

/** Rodzaj komórki z tego, co oddał `exceljs` — formułę oceniamy po jej WYNIKU. */
export function rodzajKomorki(v: unknown): RodzajKomorki {
  if (v === null || v === undefined || v === '') return '';
  if (typeof v === 'boolean') return 'b';
  if (typeof v === 'number') return 'n';
  if (typeof v === 'string') return v.trim() === '' ? '' : 't';
  if (v instanceof Date) return 't';
  if (typeof v === 'object') {
    const o = v as { result?: unknown; richText?: unknown; text?: unknown };
    if (o.result !== undefined) return rodzajKomorki(o.result);
    if (o.richText !== undefined || o.text !== undefined) return 't';
  }
  return '';
}

/** Komórka → tekst. Obsługuje to, co `exceljs` realnie oddaje poza napisem i liczbą. */
export function tekstKomorki(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v.trim();
  if (typeof v === 'number') return String(v);
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === 'object') {
    const o = v as { text?: unknown; result?: unknown; richText?: { text?: string }[]; hyperlink?: unknown };
    /** Tekst sformatowany: `richText` to tablica fragmentów — składamy je z powrotem. */
    if (Array.isArray(o.richText)) return o.richText.map((f) => f.text ?? '').join('').trim();
    if (o.text !== undefined) return tekstKomorki(o.text);
    /** Formuła: interesuje nas WYNIK, nie zapis formuły. */
    if (o.result !== undefined) return tekstKomorki(o.result);
  }
  return '';
}

/**
 * Wszystkie arkusze pliku, każdy jako tabela tekstów.
 *
 * ⚠️ WSZYSTKIE, TAKŻE PUSTE — zamówione wprost („wykryć wszystkie arkusze"). Odsianie pustych
 * znaczyłoby, że plik z danymi w drugiej zakładce wygląda na plik z jedną zakładką, a człowiek
 * nie ma jak zgadnąć, że coś ukryliśmy.
 */
export async function czytajArkusze(dane: ArrayBuffer): Promise<Arkusz[]> {
  const ExcelJS = await import('exceljs');
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(dane);

  return wb.worksheets.map((ws) => {
    const wiersze: string[][] = [];
    const rodzaje: RodzajKomorki[][] = [];
    const liczbowe = new Set<number>();
    ws.eachRow({ includeEmpty: false }, (row) => {
      const komorki: string[] = [];
      const rodzajeWiersza: RodzajKomorki[] = [];
      /**
       * ⚠️ INDEKSUJEMY NUMEREM KOLUMNY (`col`), NIE KOLEJNOŚCIĄ ODWIEDZIN.
       *
       * Z `includeEmpty: true` biblioteka odwiedza dziś także puste komórki, więc dopisywanie po
       * kolei dałoby TEN SAM wynik — sprawdzone, psując ten kod celowo: test przeszedł. Ale to
       * jest zachowanie OPCJI, nie gwarancja kształtu: przy `includeEmpty: false` (jedna litera
       * różnicy) wiersz z dziurą pośrodku przesunąłby wszystkie kolumny za nią o jedno miejsce
       * i cały import zmapowałby się o kolumnę w lewo, bez żadnego objawu.
       */
      row.eachCell({ includeEmpty: true }, (cell, col) => {
        komorki[col - 1] = tekstKomorki(cell.value);
        rodzajeWiersza[col - 1] = rodzajKomorki(cell.value);
        if (typeof cell.value === 'number') liczbowe.add(col - 1);
      });
      const pelne = Array.from({ length: komorki.length }, (_, i) => komorki[i] ?? '');
      if (pelne.some((k) => k !== '')) {
        wiersze.push(pelne);
        rodzaje.push(Array.from({ length: komorki.length }, (_, i) => rodzajeWiersza[i] ?? ''));
      }
    });
    return { nazwa: ws.name, wiersze, rodzaje, kolumnyLiczbowe: [...liczbowe].sort((a, b) => a - b) };
  });
}

/**
 * SŁOWA, PO KTÓRYCH ROZPOZNAJEMY KOLUMNĘ W NAGŁÓWKU.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ WZIĘTE Z PRAWDZIWYCH NAGŁÓWKÓW, NIE Z NASZYCH ETYKIET. Arkusz Magny ma je bez polskich
 *  znaków i z literówką: `pozwol logowac`, `wylogoj po operacji`, `grupa uzytkownikow`. Dlatego
 *  porównujemy po tekście POZBAWIONYM ogonków i szukamy fragmentu, a nie równości.
 *
 *  ⚠️ NAZWY KOLUMN SPS TEŻ SĄ NA LIŚCIE (`USERNUMBER`, `DESCR`, `ALLOWLOGIN`…), bo część plików
 *  powstaje z eksportu z bazy, a nie z ręki.
 *
 *  ⚠️ AUTOMAT TYLKO PODPOWIADA. Wynik jest widoczny w oknie i można go zmienić — mapowanie
 *  wstawione po cichu i niewidoczne byłoby najgorszym z możliwych wariantów, bo o pomyłce
 *  dowiedzielibyśmy się dopiero z bazy klienta.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
const SLOWA: Readonly<Record<KluczPola, readonly string[]>> = {
  karta: ['numer karty', 'nr karty', 'karta', 'usernumber', 'numer uzytkownika'],
  nazwa: ['imie', 'nazwisko', 'nazwa', 'opis', 'descr', 'pracownik'],
  alias: ['alias', 'inny', 'numer dodatkowy'],
  centrum: ['centrum', 'costcenter', 'mpk'],
  grupa: ['grupa', 'usergroup', 'dzial'],
  logowanie: ['pozwol logowac', 'pozwol logować', 'logowanie', 'allowlogin'],
  wyloguj: ['wylogoj', 'wyloguj', 'returntomenu', 'po operacji'],
  przegroda: ['przegrod', 'singlecomp', 'ograniczenie'],
  polityka: ['polityka', 'wydzielan', 'rationeffect'],
};

/** Bez ogonków, małymi literami, bez podwójnych spacji — do porównywania nagłówków. */
export function uprosc(s: string): string {
  return s
    .normalize('NFD')
    /** ⚠️ Zakres zapisany JAWNIE (U+0300–U+036F): literalne znaki łączące są w źródle niewidoczne. */
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ł/g, 'l')
    .replace(/Ł/g, 'l')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

export type Mapowanie = Partial<Record<KluczPola, number>>;

/**
 * Podpowiedź mapowania na podstawie nagłówków.
 *
 * ⚠️ JEDNA KOLUMNA NA JEDNO POLE — kolumna raz przypisana nie trafia do drugiego pola. Bez tego
 * nagłówek `Opis` (pasuje do „nazwa") i `Numer karty` (pasuje do „karta") byłyby w porządku, ale
 * `grupa uzytkownikow` złapałaby i „grupa", i „nazwa" po słowie `uzytkownik`.
 *
 * ⚠️ KOLEJNOŚĆ SZUKANIA IDZIE PO NASZYCH POLACH, nie po kolumnach arkusza: pierwsze pole ma
 * pierwszeństwo do kolumny, która pasuje do kilku. Numer karty jest pierwszy i to jest właściwe —
 * jego pomyłka jest najdroższa.
 */
export function podpowiedzMapowania(naglowki: readonly string[]): Mapowanie {
  const m: Mapowanie = {};
  const zajete = new Set<number>();
  for (const klucz of KLUCZE_POL) {
    const trafienie = naglowki.findIndex((h, i) => {
      if (zajete.has(i)) return false;
      const t = uprosc(h);
      return t !== '' && SLOWA[klucz].some((s) => t.includes(uprosc(s)));
    });
    if (trafienie >= 0) {
      m[klucz] = trafienie;
      zajete.add(trafienie);
    }
  }
  return m;
}

/**
 * Czy pierwszy wiersz wygląda na nagłówek.
 *
 * ⚠️ ROZSTRZYGAMY PO ROZPOZNANIU, NIE PO WYGLĄDZIE. „Wiersz z tekstem, a nie liczbami" byłoby
 * fałszywe: w arkuszu Magny KAŻDY wiersz jest tekstem, bo numery kart są tekstowe. Nagłówkiem
 * jest wiersz, w którym rozpoznajemy przynajmniej dwie nasze kolumny — czyli ten, który mówi
 * o sobie, czym jest.
 */
export function czyPierwszyToNaglowek(wiersze: readonly string[][]): boolean {
  if (wiersze.length === 0) return false;
  return Object.keys(podpowiedzMapowania(wiersze[0])).length >= 2;
}

export type WynikMapowania = {
  wiersze: { nr: number; wiersz: SurowyWiersz }[];
  /**
   * Ile wierszy odsialiśmy i dlaczego — LICZBA MUSI BYĆ WIDOCZNA NA EKRANIE.
   *
   * ⚠️⚠️ CICHE ODSIANIE JEST GROŹNIEJSZE OD BŁĘDU. „Mam 50 osób, a weszło 48" to pytanie, na
   * które portal musi umieć odpowiedzieć od razu — inaczej brakujących ludzi szuka się przez
   * porównywanie arkusza z listą pracowników.
   */
  bezKlucza: number;
  artefaktow: number;
};

/**
 * Wiersze arkusza → surowe wiersze wsadu, według wskazanego mapowania.
 *
 * ═══════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ WIERSZ BEZ NUMERU KARTY NIE JEST WIERSZEM — i to jest różnica między ARKUSZEM
 *  a WKLEJONYM TEKSTEM, nie niekonsekwencja. W arkuszu brak klucza znaczy „tu nic nie ma":
 *  Excel ciągnie zakres w dół formatowaniem i formułami, a klient sam tego nie widzi. We wklejonej
 *  linijce brak klucza znaczy „zapomniałeś numeru" i MUSI zostać błędem — dlatego odsiewamy
 *  tylko tutaj.
 *
 *  ⚠️⚠️ WARTOŚĆ LOGICZNA W KOLUMNIE KLUCZA TEŻ ZNACZY „TU NIC NIE MA". Zmierzone na pliku
 *  `Rękawiczki dostęp.xlsx`: 2498 wierszy z FAŁSZ w numerze karty, bo generująca je formuła to
 *  `IF` bez gałęzi „w przeciwnym razie". Bez tej reguły wszystkie 2498 stałyby się wierszami
 *  o numerze karty „false" — i cały import odpadał na duplikatach, dokładnie jak zgłoszono.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function wierszeZMapowania(
  wiersze: readonly string[][],
  mapowanie: Mapowanie,
  rodzaje: readonly RodzajKomorki[][] = [],
  /** Numer pierwszego wiersza W ARKUSZU — żeby komunikaty wskazywały to, co człowiek widzi w Excelu. */
  odWiersza = 1,
): WynikMapowania {
  const out: { nr: number; wiersz: SurowyWiersz }[] = [];
  let bezKlucza = 0;
  let artefaktow = 0;

  wiersze.forEach((w, i) => {
    const s = pustyWiersz();
    for (const klucz of KLUCZE_POL) {
      const kol = mapowanie[klucz];
      if (kol !== undefined) s[klucz] = w[kol] ?? '';
    }

    const kolKarty = mapowanie.karta;
    if (kolKarty !== undefined) {
      const rodzaj = rodzaje[i]?.[kolKarty] ?? '';
      if (rodzaj === 'b') {
        artefaktow += 1;
        return;
      }
      if ((w[kolKarty] ?? '').trim() === '') {
        bezKlucza += 1;
        return;
      }
    }

    out.push({ nr: odWiersza + i, wiersz: s });
  });

  return { wiersze: out, bezKlucza, artefaktow };
}

/**
 * Czy kolumna wskazana jako numer karty była w arkuszu liczbowa.
 *
 * ⚠️⚠️ TO JEDYNE OSTRZEŻENIE, KTÓREGO NIE DA SIĘ ZASTĄPIĆ POPRAWKĄ W KODZIE. Zera zniknęły
 * w pliku, u klienta, przed wysłaniem — my możemy tylko powiedzieć, że tak się stało.
 */
export function kartaMozeBycObcieta(mapowanie: Mapowanie, kolumnyLiczbowe: readonly number[]): boolean {
  const kol = mapowanie.karta;
  return kol !== undefined && kolumnyLiczbowe.includes(kol);
}
